import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { Chip } from '../../../components/ui/Chip';
import { Helper } from '../../../components/ui/Helper';
import { EmptyState } from '../../../components/ui/EmptyState';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow } from '../../../components/ui/Skeleton';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { ADMIN_PAGES, ADMIN_PATH, canSeeAdminPage, type AdminPageKey } from '../../../lib/adminRoutes';
import { ActionStream } from '../../oggi/ActionStream';
import { AdminHeader, useSyncAll } from '../shared/AdminShell';
import {
  situazioneQuery, orarioEsatto,
  type Finestra, type MesePrevisione, type ParteMese, type Situazione, type StatoSync,
} from './situazioneApi';
import '../admin.css';
import './situazione.css';

/**
 * Situazione (`/admin`): come stiamo con i soldi, ce la facciamo nei
 * prossimi mesi, e cosa va fatto adesso.
 *
 *   frase              calcolata dal server, parole e date, mai importi (R1, R6)
 *   fascia             Soldi disponibili oggi · Soldi che ci devono i clienti ·
 *                      Pagamenti scaduti non ancora fatti (ognuno porta alla sua pagina)
 *   Cose da fare oggi  lo stream di Oggi, solo Amministrazione (max 7 righe)
 *   Il conto in banca nei prossimi mesi   Entrate · Uscite · Saldo a fine mese, senza lo
 *                      scaduto (R4); la riga si apre e dice di cosa è fatto il mese
 *   Da dove vengono i numeri     aggiornamenti di Qonto e FattureInCloud, anche falliti
 *   Le altre pagine dell'Amministrazione   solo telefono, dove la sidebar non c'è
 *
 * Specifica: os-planning/amministrazione-v2/SPEC.md e r-proposta-claude.md § 2.1.
 * Nessun conto qui: tutto arriva da GET /admin/situazione (admin.situazione.ts),
 * lo stesso che scrive la mail del lunedì. La Situazione guarda sempre dal 2026
 * in poi: niente filtro dello storico.
 */
export function SituazionePage() {
  const navigate = useNavigate();
  const [finestra, setFinestra, modo] = useFinestra();
  const { data: s, isLoading, isError } = useQuery({ ...situazioneQuery(finestra), placeholderData: keepPreviousData });
  const sync = useSyncAll();

  return (
    // `adm-page` è il recinto di admin.css: le sue regole valgono solo qui dentro.
    <div className="page adm-page sit-page">
      <AdminHeader title="Situazione" crumb={false} actions={sync.button} menu={[sync.menuItem]} />

      {s && !s.sync.qonto.collegato && (
        <div className="adm-note text-warn">
          <Icon name="alert" size={12} />
          <span>Qonto non è collegato: senza, i saldi e i movimenti del conto non arrivano. <a className="underline cursor-pointer" onClick={() => navigate('/settings')}>Collegalo dalle Impostazioni</a>.</span>
        </div>
      )}

      {isError && !s ? (
        <EmptyState brand title="La Situazione non risponde" description="L'API dell'Amministrazione non ha risposto: riprova tra un momento." />
      ) : (
        <>
          <Frase testo={s?.frase} />
          {isLoading && !s ? <SkeletonKPIRow count={3} withIcon={false} /> : s && <Fascia s={s} onGo={navigate} />}

          <section className="ed-section sit-block">
            <div className="data-hd">
              <span className="t-micro">Cose da fare oggi</span>
            </div>
            <div className="card">
              <div className="card-bd card-bd--tight"><ActionStream scope="admin" max={7} /></div>
            </div>
          </section>

          <ContoNeiMesi s={s} loading={isLoading} finestra={finestra} modo={modo} onFinestra={setFinestra} />

          {s && <DaDoveVengono s={s} />}
        </>
      )}

      <AltrePagine />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La frase in cima
// ════════════════════════════════════════════════════════════════════

function Frase({ testo }: { testo: string | undefined }) {
  if (!testo) {
    return (
      <div className="sit-frase" aria-hidden="true">
        <span className="skeleton sk-sm" style={{ maxWidth: 620 }} />
      </div>
    );
  }
  return <p className="sit-frase">{testo}</p>;
}

// ════════════════════════════════════════════════════════════════════
// La fascia dei tre numeri: ognuno porta alla pagina che lo spiega.
// Ogni etichetta nomina la cosa (SPEC, regola etichette).
// ════════════════════════════════════════════════════════════════════

/** Dove porta il clic su "Pagamenti scaduti non ancora fatti": la pagina della parte più grande. */
const PAGINA_DELLO_SCADUTO: Record<string, AdminPageKey> = {
  fisco: 'fisco', fornitori: 'spese', speseFisse: 'spese', contanti: 'spese', stipendi: 'dipendenti',
};

function Fascia({ s, onGo }: { s: Situazione; onGo: (to: string) => void }) {
  const user = useAuthStore((st) => st.user);
  const sc = s.scaduti;
  let dove: AdminPageKey = PAGINA_DELLO_SCADUTO[sc.principale ?? 'fisco'] ?? 'fisco';
  if (dove === 'dipendenti' && !canSeeAdminPage(user, 'dipendenti')) dove = 'spese';
  const ritardo = s.clienti.fattureInRitardo;
  const ivaProvvisoria = sc.fisco.provvisorio > 0 && sc.fisco.iva.importo > 0;
  return (
    <div className="stat-band sit-band">
      <StatCell
        label="Soldi disponibili oggi"
        value={s.disponibili.totale}
        sub={s.disponibili.bancaAggiornataTesto}
        title={`Conti in banca più i contanti: i singoli conti sono in Banca.${s.disponibili.bancaAggiornataAl ? ` Banca aggiornata il ${orarioEsatto(s.disponibili.bancaAggiornataAl)}.` : ''}`}
        onClick={() => onGo(ADMIN_PATH.banca)}
      />
      <StatCell
        label="Soldi che ci devono i clienti"
        value={s.clienti.totale}
        sub={ritardo > 0 ? `${ritardo} ${ritardo === 1 ? 'fattura' : 'fatture'} in ritardo` : 'nessuna fattura in ritardo'}
        subAlert={ritardo > 0}
        title={`${s.clienti.fattureAperte} ${s.clienti.fattureAperte === 1 ? 'fattura' : 'fatture'} da incassare in tutto`}
        onClick={() => onGo(ADMIN_PATH.fatture)}
      />
      <StatCell
        label="Pagamenti scaduti non ancora fatti"
        value={sc.totale}
        sub={sc.totale > 0 ? sc.descrizione : 'niente di scaduto'}
        helper={sc.totale > 0 ? (
          ivaProvvisoria
            ? "È quello che risulta a sistema: l'IVA arretrata è un nostro calcolo finché il commercialista non manda gli F24. Non entra nel conto dei prossimi mesi qui sotto."
            : 'È quello che risulta a sistema, con i dati di oggi. Non entra nel conto dei prossimi mesi qui sotto.'
        ) : undefined}
        onClick={() => onGo(ADMIN_PATH[dove])}
      />
    </div>
  );
}

function StatCell({ label, value, sub, subAlert, helper, title, onClick }: {
  label: string; value: number; sub: string; subAlert?: boolean; helper?: string; title?: string; onClick: () => void;
}) {
  return (
    <div className="st-click" role="button" tabIndex={0} title={title} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className="st-label">
        {label}
        {/* La "?" spiega il numero: il clic non deve portare via dalla pagina. */}
        {helper && <span className="sit-help" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}><Helper text={helper} size={11} /></span>}
      </div>
      <div className="st-value"><span className="u">€</span><AnimatedNumber value={value} format={fmtEur} /></div>
      <div className={`st-delta${subAlert ? ' text-alert' : ''}`}>{sub}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Il conto in banca nei prossimi mesi
// ════════════════════════════════════════════════════════════════════

type Modo = 'tre' | 'sei' | 'date';

/** La finestra dei mesi sta nell'URL (`?mesi=6`, `?da=2026-10&a=2027-03`): si condivide. */
function useFinestra(): [Finestra, (f: Finestra | null) => void, Modo] {
  const [params, setParams] = useSearchParams();
  const da = params.get('da');
  const a = params.get('a');
  const mesi = Number(params.get('mesi')) || 3;
  const mese = /^\d{4}-\d{2}$/;
  const finestra: Finestra = da && a && mese.test(da) && mese.test(a) && da <= a ? { da, a } : { mesi: mesi === 6 ? 6 : 3 };
  const modo: Modo = 'da' in finestra ? 'date' : finestra.mesi === 6 ? 'sei' : 'tre';
  const set = (f: Finestra | null) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('mesi'); sp.delete('da'); sp.delete('a');
      if (f && 'da' in f) { sp.set('da', f.da); sp.set('a', f.a); }
      else if (f && f.mesi !== 3) sp.set('mesi', String(f.mesi));
      return sp;
    }, { replace: true });
  };
  return [finestra, set, modo];
}

const MESI_LUNGHI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
function meseInParole(key: string, annoOggi: number): string {
  const anno = Number(key.slice(0, 4));
  const nome = MESI_LUNGHI[Number(key.slice(5, 7)) - 1] ?? key;
  return anno === annoOggi ? nome : `${nome} ${anno}`;
}

function ContoNeiMesi({ s, loading, finestra, modo, onFinestra }: {
  s: Situazione | undefined; loading: boolean; finestra: Finestra; modo: Modo; onFinestra: (f: Finestra | null) => void;
}) {
  const [scegli, setScegli] = useState(modo === 'date');
  const [aperto, setAperto] = useState<string | null>(null);
  const p = s?.previsione;
  const annoOggi = Number((s?.oggi ?? new Date().toISOString()).slice(0, 4));
  const meseOggi = (s?.oggi ?? new Date().toISOString()).slice(0, 7);
  const titolo = 'da' in finestra
    ? `Il conto in banca da ${meseInParole(finestra.da, annoOggi)} a ${meseInParole(finestra.a, annoOggi)}`
    : `Il conto in banca nei prossimi ${finestra.mesi} mesi`;
  const modoVisto: Modo = scegli ? 'date' : modo;

  return (
    <section className="ed-section sit-block">
      <div className="data-hd sit-mesi-hd">
        <span className="t-micro">{titolo}</span>
        <div className="sit-range">
          <div className="seg-control" role="group" aria-label="Quanti mesi guardare">
            <button type="button" aria-pressed={modoVisto === 'tre'} onClick={() => { setScegli(false); onFinestra({ mesi: 3 }); }}>3 mesi</button>
            <button type="button" aria-pressed={modoVisto === 'sei'} onClick={() => { setScegli(false); onFinestra({ mesi: 6 }); }}>6 mesi</button>
            <button type="button" aria-pressed={modoVisto === 'date'} onClick={() => setScegli(true)}>Scegli le date</button>
          </div>
          {scegli && (
            <div className="sit-date">
              <input
                type="month" className="inp inp-sm" aria-label="Dal mese" min={meseOggi}
                value={'da' in finestra ? finestra.da : p?.da ?? meseOggi}
                onChange={(e) => {
                  const da = e.target.value;
                  if (!da) return;
                  const a = 'da' in finestra && finestra.a >= da ? finestra.a : p?.a && p.a >= da ? p.a : da;
                  onFinestra({ da, a });
                }}
              />
              <span className="mute-2 text-[12px]">→</span>
              <input
                type="month" className="inp inp-sm" aria-label="Al mese" min={'da' in finestra ? finestra.da : meseOggi}
                value={'da' in finestra ? finestra.a : p?.a ?? meseOggi}
                onChange={(e) => {
                  const a = e.target.value;
                  if (!a) return;
                  const da = 'da' in finestra ? finestra.da : p?.da ?? meseOggi;
                  onFinestra({ da: da <= a ? da : a, a });
                }}
              />
            </div>
          )}
        </div>
      </div>

      {loading && !p ? (
        <div className="skeleton-col"><span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" /></div>
      ) : !p || p.mesi.length === 0 ? (
        <EmptyState icon="chart" title="Nessun mese da mostrare" description="Scegli un intervallo che parta da questo mese in avanti." />
      ) : (
        <>
          <table className="tbl tbl--ed tbl--card sit-mesi">
            <thead>
              <tr>
                <th>Mese</th>
                <th className="num">Entrate previste</th>
                <th className="num">Uscite previste</th>
                <th className="num">Saldo previsto a fine mese</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {p.mesi.map((m) => {
                const open = aperto === m.mese;
                const toggle = () => setAperto(open ? null : m.mese);
                return (
                  <Fragment key={m.mese}>
                    <tr
                      className="cursor-pointer"
                      aria-expanded={open}
                      tabIndex={0}
                      onClick={toggle}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    >
                      <td data-m="hd">
                        <span className="font-semibold">{m.nome}</span>
                        {m.dal && <span className="mute" title={`Da oggi, ${fmtDate(m.dal)}: quello che è già entrato e uscito sta nel saldo di oggi`}> da oggi</span>}
                        {m.piuStretto && p.mesi.length > 1 && <Chip tone={m.saldoFine < 0 ? 'alert' : 'warn'} className="sit-chip">il saldo più basso</Chip>}
                      </td>
                      <td data-m="key" data-l="Entrate previste" className="num mono">€ {fmtEur(m.entra.totale)}</td>
                      <td data-m="key" data-l="Uscite previste" className="num mono">€ {fmtEur(m.esce.totale)}</td>
                      <td data-m="row" data-l="Saldo previsto a fine mese" className="num mono font-semibold">
                        {/* Lo span serve: .tbl tbody td fissa il colore, l'utility sul td perderebbe. */}
                        <span className={m.saldoFine < 0 ? 'text-alert' : undefined}>{m.saldoFine < 0 ? '− ' : ''}€ {fmtEur(Math.abs(m.saldoFine))}</span>
                      </td>
                      <td data-m="off" className="sit-chev"><Icon name={open ? 'chevronU' : 'chevronD'} size={12} className="mute" /></td>
                    </tr>
                    {open && (
                      <tr className="adm-subrow">
                        <td colSpan={5}><DettaglioMese m={m} /></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="adm-note sit-nota">
            <Icon name="help" size={12} className="mute" />
            <span>
              Qui entra solo ciò che deve ancora scadere; quello già scaduto è in «Pagamenti scaduti non ancora fatti».
              {p.inizioFinestra !== p.partenza.totale && ` La previsione parte dal saldo previsto all'inizio di ${meseInParole(p.da, annoOggi)}, con quello che entra ed esce da oggi ad allora.`}
            </span>
          </div>
        </>
      )}
    </section>
  );
}

/** Di cosa è fatto un mese: cosa entra (fatture, canoni, contanti) e cosa esce (spese e fornitori, stipendi, tasse). */
function DettaglioMese({ m }: { m: MesePrevisione }) {
  return (
    <div className="adm-sublist sit-dettaglio">
      <div>
        <div className="t-micro sit-det-hd">Entrate previste a{m.nome.toLowerCase()}</div>
        {m.entra.parti.map((pt) => <RigaParte key={pt.chiave} p={pt} />)}
      </div>
      <div>
        <div className="t-micro sit-det-hd">Uscite previste a{m.nome.toLowerCase()}</div>
        {m.esce.parti.map((pt) => <RigaParte key={pt.chiave} p={pt} />)}
      </div>
    </div>
  );
}

function RigaParte({ p }: { p: ParteMese }) {
  const vuota = p.importo === 0;
  return (
    <div className={`adm-line${vuota ? ' mute' : ''}`}>
      <div className="flex-1 min-w-0">
        <div>{p.etichetta}</div>
        {p.voci.length > 0 && (
          <div className="text-[12px] mute sit-voci">
            {p.voci.map((v, i) => (
              <Fragment key={`${v.nome}-${i}`}>
                {i > 0 && ' · '}
                {v.nome} € {fmtEur(v.importo)}{v.stima && ' (stima)'}
              </Fragment>
            ))}
            {p.altre > 0 && ` · e ${p.altre === 1 ? "un'altra voce" : `altre ${p.altre} voci`}`}
          </div>
        )}
      </div>
      <div className="mono font-semibold text-right">€ {fmtEur(p.importo)}</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Da dove vengono i numeri
// ════════════════════════════════════════════════════════════════════

function DaDoveVengono({ s }: { s: Situazione }) {
  const persone = s.personale;
  return (
    <section className="ed-section sit-block">
      <div className="data-hd"><span className="t-micro">Da dove vengono i numeri</span></div>
      <div className="sit-fonti">
        <Fonte icon="landmark" nome="Banca (Qonto)" stato={s.sync.qonto} />
        <Fonte icon="invoice" nome="Fatture (FattureInCloud)" stato={s.sync.fic} />
        <div className="sit-fonte">
          <Icon name="user" size={12} className="mute" />
          <span>
            <strong>Stipendi</strong>:{' '}
            {persone.senzaStipendio.length === 0
              ? `dalle spese fisse delle ${persone.persone} persone in busta paga (pagina Dipendenti).`
              : `mancano ancora fra le spese fisse quelli di ${persone.senzaStipendio.join(', ')}: nel conto dei mesi non ci sono.`}
          </span>
        </div>
        <div className="sit-fonte">
          <Icon name="percent" size={12} className="mute" />
          <span>
            <strong>Tasse</strong>: le cifre segnate «stima» sono calcoli nostri e possono cambiare quando arrivano gli F24 del commercialista.
          </span>
        </div>
      </div>
    </section>
  );
}

function Fonte({ icon, nome, stato }: { icon: IconName; nome: string; stato: StatoSync }) {
  const errore = stato.errore ?? stato.erroreUltime24h;
  return (
    <div className={`sit-fonte${stato.problema ? ' text-alert' : ''}`} title={errore ? `Ultimo errore: ${errore}` : orarioEsatto(stato.ultimoOk)}>
      <Icon name={icon} size={12} className={stato.problema ? '' : 'mute'} />
      <span><strong>{nome}</strong>: {stato.testo}.</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Telefono: senza sidebar, le altre pagine si raggiungono da qui
// (r-proposta-claude.md § 1.3). Sul computer ci pensa la sidebar.
// ════════════════════════════════════════════════════════════════════
function AltrePagine() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const pages = ADMIN_PAGES.filter((p) => p.key !== 'situazione' && canSeeAdminPage(user, p));
  return (
    <div className="ed-section m-only adm-altre">
      <div className="data-hd">
        <span className="t-micro">Le altre pagine dell'Amministrazione</span>
      </div>
      {pages.map((p) => (
        <div
          key={p.key}
          className="adm-line cursor-pointer"
          role="link"
          tabIndex={0}
          onClick={() => navigate(p.to)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(p.to); }}
        >
          <div className="icon-tile sm"><Icon name={p.icon} size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{p.label}</div>
            <div className="mute text-[12px]">{p.sub}</div>
          </div>
          <Icon name="chevronR" size={12} className="mute" />
        </div>
      ))}
    </div>
  );
}
